import type { CSSProperties } from 'react';
import type { LauncherRect } from './launcher-geometry.js';
import { viewportSize } from './launcher-geometry.js';

export type GreetingLayoutMode = 'quadrant' | 'edge';

export type ScreenQuadrant =
  | 'top_left'
  | 'top_right'
  | 'bottom_left'
  | 'bottom_right';

type CaretSide = 'top' | 'bottom';

export type GreetingPlacement = {
  wrap: CSSProperties;
  caret: CaretSide;
  caretAlign: 'start' | 'end';
  label: ScreenQuadrant | string;
};

const MARGIN = 12;
const GAP = 10;
const POPUP_MAX_W = 280;
const POPUP_EST_H = 150;

export function resolveQuadrant(rect: LauncherRect): ScreenQuadrant {
  const { w: vw, h: vh } = viewportSize();
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  const horizontal = cx < vw / 2 ? 'left' : 'right';
  const vertical = cy < vh / 2 ? 'top' : 'bottom';
  return `${vertical}_${horizontal}` as ScreenQuadrant;
}

/** Upper half (or not enough room above) → greeting below the launcher. */
export function shouldPlaceBelow(rect: LauncherRect): boolean {
  const { h: vh } = viewportSize();
  const cy = rect.y + rect.h / 2;
  const spaceAbove = rect.y - MARGIN;
  const spaceBelow = vh - (rect.y + rect.h) - MARGIN;
  if (cy < vh / 2) return true;
  if (spaceAbove < POPUP_EST_H && spaceBelow >= spaceAbove) return true;
  return false;
}

function horizontalAlign(
  rect: LauncherRect,
  preferLeft: boolean,
  maxW: number,
): { left?: number; right?: number } {
  const { w: vw } = viewportSize();
  if (preferLeft) {
    return { left: Math.max(MARGIN, Math.min(rect.x, vw - maxW - MARGIN)) };
  }
  return {
    right: Math.max(
      MARGIN,
      Math.min(vw - (rect.x + rect.w), vw - maxW - MARGIN),
    ),
  };
}

function verticalAlign(
  rect: LauncherRect,
  preferBelow: boolean,
): { top?: number; bottom?: number; caret: CaretSide } {
  const { h: vh } = viewportSize();
  if (preferBelow) {
    const top = Math.min(
      rect.y + rect.h + GAP,
      Math.max(MARGIN, vh - POPUP_EST_H - MARGIN),
    );
    return { top: Math.max(MARGIN, top), caret: 'top' };
  }
  const bottom = Math.min(
    vh - rect.y + GAP,
    Math.max(MARGIN, vh - POPUP_EST_H - MARGIN),
  );
  return { bottom: Math.max(MARGIN, bottom), caret: 'bottom' };
}

function placeQuadrant(rect: LauncherRect): GreetingPlacement {
  const { w: vw } = viewportSize();
  const quadrant = resolveQuadrant(rect);
  const maxW = Math.min(POPUP_MAX_W, vw - MARGIN * 2);
  const preferLeft = quadrant === 'top_left' || quadrant === 'bottom_left';
  const preferBelow = shouldPlaceBelow(rect);
  const horiz = horizontalAlign(rect, preferLeft, maxW);
  const vert = verticalAlign(rect, preferBelow);
  return {
    wrap: {
      position: 'fixed',
      zIndex: 2147483110,
      maxWidth: maxW,
      width: maxW,
      display: 'flex',
      flexDirection: 'column',
      pointerEvents: 'auto',
      ...horiz,
      ...(vert.top !== undefined ? { top: vert.top } : {}),
      ...(vert.bottom !== undefined ? { bottom: vert.bottom } : {}),
    },
    caret: vert.caret,
    caretAlign: preferLeft ? 'start' : 'end',
    label: quadrant,
  };
}

function placeEdge(rect: LauncherRect): GreetingPlacement {
  const { w: vw } = viewportSize();
  const preferLeft = rect.x + rect.w / 2 < vw / 2;
  const preferBelow = shouldPlaceBelow(rect);
  const maxW = Math.min(POPUP_MAX_W, vw - MARGIN * 2 - rect.w - GAP);
  const vert = verticalAlign(rect, preferBelow);
  const horiz = preferLeft
    ? { left: Math.min(rect.x + rect.w + GAP, vw - maxW - MARGIN) }
    : horizontalAlign(rect, false, maxW);

  return {
    wrap: {
      position: 'fixed',
      zIndex: 2147483110,
      maxWidth: maxW,
      width: maxW,
      display: 'flex',
      flexDirection: 'column',
      pointerEvents: 'auto',
      ...horiz,
      ...(vert.top !== undefined ? { top: vert.top } : {}),
      ...(vert.bottom !== undefined ? { bottom: vert.bottom } : {}),
    },
    caret: vert.caret,
    caretAlign: preferLeft ? 'start' : 'end',
    label: `edge_${preferLeft ? 'left' : 'right'}_${preferBelow ? 'below' : 'above'}`,
  };
}

export function resolveGreetingPlacement(
  rect: LauncherRect,
  mode: GreetingLayoutMode,
): GreetingPlacement {
  return mode === 'edge' ? placeEdge(rect) : placeQuadrant(rect);
}
